import BadgeMask from '-/Badge/BadgeMask';
import Avatar from '-/Custom/Avatar';
import { Stack, useRouter } from 'expo-router';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import FadeInContent from '@/components/Animation/FadeInContent';
import FooterActionButton from '@/components/Custom/FooterActionButton';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import SearchBar from '@/components/Custom/SearchBar';
import type { PeopleFilter } from '@/components/People/member-type';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import CheckboxRow from '@/components/UI/CheckboxRow';
import RadioGroup from '@/components/UI/RadioGroup';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';
import type { OrganizationMemberFilterMenuOption } from '@/screens/OrganizationDetailMembers/OrganizationMemberFilterMenu';
import useMemberFilterStore, {
  clearActiveMemberFilterValues,
  type MemberFilterValueKey,
  setActiveMemberFilterMemberType,
  setActiveMemberFilterValueIds,
  toggleActiveMemberFilterValue,
  useMemberFilterActiveCount,
  useMemberFilterValueCount,
  useMemberFilterValueSelected,
  useMemberFilterValues,
} from '@/stores/useMemberFilterStore';
import { filterBySearchText } from '@/utils/filterBySearchText';

const TEAM_FILTER_LIMIT = 250;
const BADGE_FILTER_LIMIT = 250;
const MEMBER_TYPE_OPTIONS: ReadonlyArray<{
  label: string;
  value: PeopleFilter;
}> = [
  { label: 'All', value: 'all' },
  { label: 'Participants', value: 'participant' },
  { label: 'Non Participants', value: 'non_participant' },
];

const GetMemberActionOrganizationFilters = graphql(/* GraphQL */ `
  query GetMemberActionOrganizationFilters(
    $organizationId: uuid!
    $teamFilterLimit: Int!
    $badgeFilterLimit: Int!
  ) {
    team(
      where: {
        root_event_id: { _eq: $organizationId }
        _or: [
          { permissions: { _has_key: "edit_self" } }
          { root_event: { permissions: { _has_key: "see_others" } } }
        ]
      }
      limit: $teamFilterLimit
      order_by: { title: asc }
    ) {
      id
      title
      avatar_id
      home_primary_color
    }
    relevant_badge(
      args: { event_id: $organizationId }
      limit: $badgeFilterLimit
      order_by: { title: asc }
    ) {
      id
      title
      color
    }
  }
`);

const GetMemberActionTeamFilters = graphql(/* GraphQL */ `
  query GetMemberActionTeamFilters($teamId: uuid!, $badgeFilterLimit: Int!) {
    relevant_badge(
      args: { team_id: $teamId }
      limit: $badgeFilterLimit
      order_by: { title: asc }
    ) {
      id
      title
      color
    }
  }
`);

export default function MemberActionFilter() {
  const { back } = useRouter();
  const [searchTerm, setSearchTerm] = useState('');
  const activeFilter = useMemberFilterStore((state) => state.activeFilter);
  const organizationId = activeFilter?.organizationId ?? null;
  const targetKey = activeFilter?.targetKey ?? '';
  const includeTeams = activeFilter?.includeTeams ?? false;
  const includeMemberTypes = activeFilter?.includeMemberTypes ?? false;
  const teamId = activeFilter?.teamId ?? null;
  const testIDPrefix = activeFilter?.testIDPrefix ?? 'MemberAction';
  const [{ data: organizationData, fetching: fetchingOrganizationFilters }] =
    useQuery({
      query: GetMemberActionOrganizationFilters,
      variables: {
        organizationId: organizationId || '',
        teamFilterLimit: TEAM_FILTER_LIMIT,
        badgeFilterLimit: BADGE_FILTER_LIMIT,
      },
      requestPolicy: 'cache-and-network',
      pause: !organizationId || Boolean(teamId),
    });
  const [{ data: teamData, fetching: fetchingTeamFilters }] = useQuery({
    query: GetMemberActionTeamFilters,
    variables: {
      teamId: teamId || '',
      badgeFilterLimit: BADGE_FILTER_LIMIT,
    },
    requestPolicy: 'cache-and-network',
    pause: !teamId,
  });

  const teamOptions: OrganizationMemberFilterMenuOption[] = (
    organizationData?.team ?? []
  ).map((team) => ({
    avatarId: team.avatar_id ?? null,
    color: team.home_primary_color ?? null,
    id: team.id,
    label: team.title ?? 'Untitled Team',
  }));
  const filterBadges = teamId
    ? (teamData?.relevant_badge ?? [])
    : (organizationData?.relevant_badge ?? []);
  const badgeOptions: OrganizationMemberFilterMenuOption[] = filterBadges.map(
    (badge) => ({
      color: badge.color ?? null,
      id: badge.id,
      label: badge.title ?? 'Untitled Badge',
    }),
  );
  const visibleTeamOptions = filterBySearchText({
    getText: (option) => option.label,
    items: teamOptions,
    searchText: searchTerm,
  });
  const visibleBadgeOptions = filterBySearchText({
    getText: (option) => option.label,
    items: badgeOptions,
    searchText: searchTerm,
  });
  const fetching = teamId ? fetchingTeamFilters : fetchingOrganizationFilters;
  const onPressClearFilters = () => {
    clearActiveMemberFilterValues();
    setSearchTerm('');
  };

  const onPressDone = () => {
    back();
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Filters' }} />
      <FadeInContent fetching={fetching}>
        <Box direction="vertical" flex style={styles.container}>
          <ScrollView
            contentContainerStyle={styles.content}
            contentInsetAdjustmentBehavior="automatic"
            keyboardShouldPersistTaps="always"
          >
            <Box direction="vertical" space="sm" px="md" py="sm">
              <Box
                direction="horizontal"
                align="center"
                justify="space-between"
                space="md"
              >
                <MemberActionFilterStatus targetKey={targetKey} />
                <MemberActionFilterClearButton
                  targetKey={targetKey}
                  testID={`${testIDPrefix}FilterClearButton`}
                  onPress={onPressClearFilters}
                />
              </Box>
              <SearchBar
                value={searchTerm}
                onChangeText={setSearchTerm}
                onPressCancel={() => {
                  setSearchTerm('');
                }}
                placeholder={
                  includeTeams
                    ? 'Search Teams and Badges...'
                    : 'Search Badges...'
                }
              />
            </Box>
            {includeTeams ? (
              <MemberActionFilterSection
                allLabel="All Teams"
                emptyLabel={
                  teamOptions.length ? 'No matching teams' : 'No teams'
                }
                label="Teams"
                options={visibleTeamOptions}
                targetKey={targetKey}
                testID={`${testIDPrefix}FilterTeam`}
                valueKey="teamIds"
                renderPreview={(option) => (
                  <Avatar
                    id={option.avatarId ?? ''}
                    size="sm"
                    color={option.color ?? ''}
                    name={option.label}
                  />
                )}
              />
            ) : null}
            {includeMemberTypes ? (
              <MemberTypeFilterSection
                targetKey={targetKey}
                testID={`${testIDPrefix}FilterMemberType`}
              />
            ) : null}
            <MemberActionFilterSection
              allLabel="All Badges"
              emptyLabel={
                badgeOptions.length ? 'No matching badges' : 'No badges'
              }
              label="Badges"
              options={visibleBadgeOptions}
              targetKey={targetKey}
              testID={`${testIDPrefix}FilterBadge`}
              valueKey="badgeIds"
              renderPreview={(option) => (
                <BadgeMask size="sm" color={option.color ?? undefined}>
                  <Avatar
                    ring={false}
                    size="sm"
                    color={option.color ?? ''}
                    name={option.label}
                  />
                </BadgeMask>
              )}
            />
          </ScrollView>
          <ModalKeyboardStickyView>
            <FooterActionButton
              label="Done"
              testID={`${testIDPrefix}FilterDoneButton`}
              onPress={onPressDone}
            />
          </ModalKeyboardStickyView>
        </Box>
      </FadeInContent>
    </>
  );
}

function MemberTypeFilterSection({
  targetKey,
  testID,
}: {
  targetKey: string;
  testID: string;
}) {
  const memberType = useMemberFilterValues(targetKey).memberType;
  const selectedLabel =
    MEMBER_TYPE_OPTIONS.find((option) => option.value === memberType)?.label ??
    'All';

  return (
    <Box direction="vertical" py="xs">
      <Box
        direction="horizontal"
        align="center"
        justify="space-between"
        px="md"
        py="sm"
      >
        <Text style={styles.label}>Member Type</Text>
        <Text size="xs" variant="typographySecondary">
          {selectedLabel}
        </Text>
      </Box>
      <RadioGroup
        value={memberType}
        onValueChange={(value) => {
          if (
            value === 'all' ||
            value === 'participant' ||
            value === 'non_participant'
          ) {
            setActiveMemberFilterMemberType(value);
          }
        }}
      >
        {MEMBER_TYPE_OPTIONS.map((option) => (
          <RadioGroup.Option
            key={option.value}
            accessibilityLabel={option.label}
            style={memberType === option.value ? styles.selectedRow : undefined}
            testID={`${testID}-${option.value}`}
            value={option.value}
          >
            <Text numberOfLines={1}>{option.label}</Text>
          </RadioGroup.Option>
        ))}
      </RadioGroup>
    </Box>
  );
}

function MemberActionFilterStatus({ targetKey }: { targetKey: string }) {
  const activeFilterCount = useMemberFilterActiveCount(targetKey);
  const filterStatus = activeFilterCount
    ? `${activeFilterCount} active`
    : 'All members';

  return (
    <Text variant="typographySecondary" numberOfLines={1}>
      {filterStatus}
    </Text>
  );
}

function MemberActionFilterClearButton({
  onPress,
  targetKey,
  testID,
}: {
  onPress: () => void;
  targetKey: string;
  testID: string;
}) {
  const activeFilterCount = useMemberFilterActiveCount(targetKey);

  if (!activeFilterCount) {
    return null;
  }

  return (
    <ActionButton
      forceLabel
      icon="close-circle-outline"
      label="Clear"
      size="sm"
      testID={testID}
      variant="secondary"
      onPress={onPress}
    />
  );
}

function MemberActionFilterSection({
  allLabel,
  emptyLabel,
  label,
  options,
  renderPreview,
  targetKey,
  testID,
  valueKey,
}: {
  allLabel: string;
  emptyLabel: string;
  label: string;
  options: OrganizationMemberFilterMenuOption[];
  renderPreview: (option: OrganizationMemberFilterMenuOption) => ReactElement;
  targetKey: string;
  testID: string;
  valueKey: MemberFilterValueKey;
}) {
  return (
    <Box direction="vertical" py="xs">
      <MemberActionFilterSectionHeader
        label={label}
        targetKey={targetKey}
        valueKey={valueKey}
      />
      <MemberActionFilterAllRow
        allLabel={allLabel}
        targetKey={targetKey}
        testID={`${testID}All`}
        valueKey={valueKey}
      />
      {options.length ? (
        options.map((option) => (
          <MemberActionFilterOptionRow
            key={option.id}
            option={option}
            renderPreview={renderPreview}
            targetKey={targetKey}
            testID={`${testID}-${option.id}`}
            valueKey={valueKey}
          />
        ))
      ) : (
        <Text px="md" py="sm" variant="typographySecondary">
          {emptyLabel}
        </Text>
      )}
    </Box>
  );
}

function MemberActionFilterSectionHeader({
  label,
  targetKey,
  valueKey,
}: {
  label: string;
  targetKey: string;
  valueKey: MemberFilterValueKey;
}) {
  const selectedCount = useMemberFilterValueCount({ targetKey, valueKey });
  const sectionStatus = selectedCount ? `${selectedCount} selected` : 'All';

  return (
    <Box
      direction="horizontal"
      align="center"
      justify="space-between"
      px="md"
      py="sm"
    >
      <Text style={styles.label}>{label}</Text>
      <Text size="xs" variant="typographySecondary">
        {sectionStatus}
      </Text>
    </Box>
  );
}

function MemberActionFilterAllRow({
  allLabel,
  targetKey,
  testID,
  valueKey,
}: {
  allLabel: string;
  targetKey: string;
  testID: string;
  valueKey: MemberFilterValueKey;
}) {
  const selectedCount = useMemberFilterValueCount({ targetKey, valueKey });
  const selected = selectedCount === 0;

  return (
    <CheckboxRow
      rounded
      style={selected ? styles.selectedRow : undefined}
      value={selected}
      testID={testID}
      onValueChange={() => {
        setActiveMemberFilterValueIds({ ids: [], valueKey });
      }}
    >
      <Text numberOfLines={1}>{allLabel}</Text>
    </CheckboxRow>
  );
}

function MemberActionFilterOptionRow({
  option,
  renderPreview,
  targetKey,
  testID,
  valueKey,
}: {
  option: OrganizationMemberFilterMenuOption;
  renderPreview: (option: OrganizationMemberFilterMenuOption) => ReactElement;
  targetKey: string;
  testID: string;
  valueKey: MemberFilterValueKey;
}) {
  const selected = useMemberFilterValueSelected({
    targetKey,
    value: option.id,
    valueKey,
  });

  return (
    <CheckboxRow
      rounded
      style={selected ? styles.selectedRow : undefined}
      value={selected}
      testID={testID}
      onValueChange={(nextSelected) => {
        toggleActiveMemberFilterValue({
          selected: nextSelected,
          value: option.id,
          valueKey,
        });
      }}
    >
      <Box direction="horizontal" align="center" space="md" minWidth={0}>
        {renderPreview(option)}
        <Text numberOfLines={1} flexShrink={1}>
          {option.label}
        </Text>
      </Box>
    </CheckboxRow>
  );
}

const styles = StyleSheet.create((theme) => ({
  container: {
    backgroundColor: theme.colors.surface,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: theme.space('lg'),
    paddingBottom: theme.space('lg'),
  },
  label: {
    color: theme.colors.typographySecondary,
    fontSize: theme.fontSize.md,
  },
  selectedRow: {
    backgroundColor: theme.colors.surfaceSecondary,
  },
}));
